import { defineStore } from "pinia"
import { computed, inject, ref } from "vue"
import { useFetchWrapper } from "@core/composables"
import type {
    SubscriptionOfferItem,
    SubscriptionModuleItem,
    SubscriptionOfferSourceDetail,
    SourcePlanItem,
    SummaryDetailItem,
    FormPaymentUserForm,
    FormPaymentCompanyForm,
} from "@modules/abonnement/types/subscription"
import type { SubscriptionOffersResponse } from "@modules/abonnement/types/subscription"
import type { Services } from "@services"

// ================================
// TYPES
// ================================

export type OfferFormatted = SubscriptionOfferItem & {
    name: string
    commitmentPrice: number | null
    selected: boolean
}

export type ModuleFormatted = SubscriptionModuleItem & {
    name: string
    display_name: string
}

type OffersRecord = NonNullable<SubscriptionOffersResponse["offers"]>
type ModulesRecord = NonNullable<SubscriptionOffersResponse["modules"]>

/** Étapes du parcours persistables (sans Success/Failure). */
export type SubscriptionFormPersistedStep =
    | "FormPaymentIntro"
    | "FormPaymentOffer"
    | "FormPaymentSources"
    | "FormPaymentRecap"

/** Données du formulaire d'inscription persistées en localStorage. */
export type SubscriptionFormPersisted = {
    step: SubscriptionFormPersistedStep
    introFormUser: FormPaymentUserForm
    introFormCompany: FormPaymentCompanyForm
    offerSelected: string
    moduleSelected: string[]
    commitment: boolean
    offerSourceSelected: string
    paymentContext: {
        user?: Record<string, unknown>
        company?: Record<string, unknown>
        pipedrive?: Record<string, unknown>
    }
    verifyUserResult: boolean | null
}

const SUBSCRIPTION_FORM_STORAGE_KEY = "subscription_form_draft"

/** Libellés récap (ex-`useI18n` local) — évite `useI18n` dans le store (incompatible hors setup / après await). */
const SUBSCRIPTION_FORM_I18N = {
    sources: "sources",
    commitment: "Avec engagement 12 mois",
    no_commitment: "Sans engagement",
} as const

function formatExtraSourcesLabel(nbSources: number): string {
    return `+ ${nbSources} sources`
}

/**
 * Store du formulaire d'abonnement : offres, modules, sélections, contexte de paiement.
 * Centralise les données, la logique de sélection et les appels services (redirectToPayment).
 * La machine ne gère que la navigation d'étape ; tout le reste est ici.
 */
export const useSubscriptionFormStore = defineStore("subscription-form", () => {
    const services = inject<Services>("services")

    // ==================
    // STATE
    // ==================

    /** Offres disponibles (clé = nom d'offre). */
    const offers = ref<OffersRecord>({})

    /** Modules disponibles (clé = nom de module). */
    const modules = ref<ModulesRecord>({})

    /** Nom de l'offre sélectionnée. */
    const offerSelected = ref<string>("")

    /** Noms des modules sélectionnés. */
    const moduleSelected = ref<string[]>([])

    /** Engagement 12 mois (true) ou sans engagement (false). */
    const commitment = ref<boolean>(true)

    /** Id du plan de sources sélectionné (étape 2). */
    const offerSourceSelected = ref<string>("")

    /** Contexte pour l'appel checkout (user, company, pipedrive). */
    const paymentContext = ref<{
        user?: Record<string, unknown>
        company?: Record<string, unknown>
        pipedrive?: Record<string, unknown>
    }>({})

    /** Formulaire intro (étape 1) : utilisateur et organisation. */
    const introFormUser = ref<FormPaymentUserForm>({
        firstname: "",
        lastname: "",
        username: "",
        phone: "",
        sector: null,
        agree: false,
    })
    const introFormCompany = ref<FormPaymentCompanyForm>({
        name: "",
        type: "entreprise",
    })

    /** Résultat de la vérification email (true = compte existant, false = inexistant, null = pas encore vérifié). */
    const verifyUserResult = ref<boolean | null>(null)

    /**
     * Normalise offer_sources (tableau legacy ou record API) en liste { id, sources, price, commitmentPrice }.
     */
    function normalizeOfferSources(
        offer: SubscriptionOfferItem | undefined
    ): Array<{
        id: string
        sources: number
        price: number
        commitmentPrice?: number
    }> {
        if (!offer || !("offer_sources" in offer) || !offer.offer_sources)
            return []
        const raw = offer.offer_sources
        if (Array.isArray(raw)) {
            return raw.map((s) => ({
                id: s.id,
                sources: s.sources,
                price: s.price,
                commitmentPrice: s.commitmentPrice,
            }))
        }
        return Object.entries(raw).map(([name, detail]) => ({
            id: name,
            sources:
                Number((detail as SubscriptionOfferSourceDetail).nb_sources) ||
                0,
            price: (detail as SubscriptionOfferSourceDetail).price,
            commitmentPrice:
                (detail as SubscriptionOfferSourceDetail).commitmentPrice ??
                undefined,
        }))
    }

    // ==================
    // GETTERS
    // ==================

    /** Offres formatées pour l'affichage (prix engagement, sélection, tri par prix). */
    const getOffersFormatted = computed((): OfferFormatted[] => {
        const raw = offers.value
        if (!raw || !Object.keys(raw).length) return []
        return Object.entries(raw)
            .map(([offerName, offer]) => ({
                ...offer,
                name: offerName,
                commitmentPrice: commitment.value
                    ? (offer.commitmentPrice ?? null)
                    : null,
                selected: offerName === offerSelected.value,
            }))
            .sort((a, b) => a.price - b.price)
    })

    /** Modules formatés pour l'affichage (filtrés par compatibilité offre, display_name). */
    const getModulesFormatted = computed((): ModuleFormatted[] => {
        const raw = modules.value
        const selectedTag = offerSelected.value
            ? offers.value[offerSelected.value]?.tag
            : undefined
        if (!raw || !Object.keys(raw).length) return []
        return Object.entries(raw)
            .filter(([, mod]) => {
                if (mod.onlyOffer?.length) {
                    return selectedTag && mod.onlyOffer.includes(selectedTag)
                }
                return true
            })
            .map(([moduleName, mod]) => ({
                ...mod,
                name: moduleName,
                display_name: mod.name,
            }))
    })

    /**
     * Pourcentage max d'économie (engagement) pour l'affichage.
     * L'API renvoie discountPer en ratio (0–1) ; on normalise en 0–100.
     * Si discountPer absent, on le déduit de price et commitmentPrice.
     */
    const getMaxDiscountPercent = computed(() => {
        return getOffersFormatted.value.reduce((acc, offer) => {
            let per = offer.discountPer ?? 0
            if (per > 0 && per <= 1) per = per * 100
            if (
                per <= 0 &&
                offer.price > 0 &&
                offer.commitmentPrice != null &&
                offer.commitmentPrice < offer.price
            ) {
                per =
                    ((offer.price - offer.commitmentPrice) / offer.price) * 100
            }
            return per > acc ? per : acc
        }, 0)
    })

    /** Plans de sources pour l'offre sélectionnée (pour l'étape 2). Triés par nombre de sources (croissant) pour que le plan 30 sources soit le plan par défaut. */
    const getSourcesPlans = computed((): SourcePlanItem[] => {
        const offer = offerSelected.value
            ? offers.value[offerSelected.value]
            : undefined
        const offerSources = normalizeOfferSources(offer)
        if (offerSources.length) {
            const mapped = offerSources.map((s) => ({
                id: s.id,
                sources: s.sources,
                price: commitment.value
                    ? (s.commitmentPrice ?? s.price)
                    : s.price,
            }))
            return mapped.sort((a, b) => a.sources - b.sources)
        }
        return [{ id: "default", sources: 30, price: 0 }]
    })

    /** IDs des modules sélectionnés pour le checkout (plans avec interval). */
    const getModuleIds = computed((): string[] => {
        const ids: string[] = []
        const interval = commitment.value ? "year" : "month"
        Object.entries(modules.value).forEach(([key, mod]) => {
            if (
                moduleSelected.value.includes(key) &&
                "plans" in mod &&
                Array.isArray(mod.plans)
            ) {
                const plan = mod.plans.find(
                    (p: { interval?: string }) => p.interval === interval
                )
                if (plan?.id) ids.push(plan.id)
            }
        })
        return ids
    })

    /** Id du plan Stripe à envoyer au checkout (price_xxx). Résout le nom d'offre source vers l'id si besoin. */
    const getCheckoutPlanId = computed((): string => {
        const selectedId =
            offerSourceSelected.value || getSourcesPlans.value[0]?.id
        if (!selectedId || !offerSelected.value) return selectedId ?? ""
        const offer = offers.value[offerSelected.value]
        if (!offer?.offer_sources) return selectedId
        const raw = offer.offer_sources
        if (Array.isArray(raw)) return selectedId
        const detail = raw[selectedId] as
            | SubscriptionOfferSourceDetail
            | undefined
        if (!detail?.plans?.length) return selectedId
        const interval = commitment.value ? "year" : "month"
        const plan = detail.plans.find((p) => p.interval === interval)
        return plan?.id ?? selectedId
    })

    /** Résumé (plan + détails) pour la colonne récap. */
    const getSummary = computed(() => {
        const details: SummaryDetailItem[] = []
        const offerName = offerSelected.value
        const offer = offerName ? offers.value[offerName] : undefined
        if (offer) {
            const price = commitment.value
                ? (offer.commitmentPrice ?? offer.price)
                : offer.price
            const sourcesLabel =
                "sources" in offer ? String(offer.sources) : "30"
            details.push({
                name: `${offerName} ${sourcesLabel} ${SUBSCRIPTION_FORM_I18N.sources} • ${commitment.value ? SUBSCRIPTION_FORM_I18N.commitment : SUBSCRIPTION_FORM_I18N.no_commitment}`,
                price: Number(price),
                allowModify: true,
            })
        }
        Object.entries(modules.value).forEach(([key, mod]) => {
            if (moduleSelected.value.includes(key)) {
                details.push({
                    name: mod.name,
                    price: mod.price,
                    allowModify: true,
                })
            }
        })
        const sourceId = offerSourceSelected.value
        if (sourceId && offer) {
            const offerSources = normalizeOfferSources(offer)
            const extra = offerSources.find((s) => s.id === sourceId)
            const baseSources = "sources" in offer ? Number(offer.sources) : 30
            if (extra && extra.sources > baseSources) {
                const extraPrice = commitment.value
                    ? (extra.commitmentPrice ?? extra.price) -
                      (offer.commitmentPrice ?? offer.price)
                    : extra.price - offer.price
                details.push({
                    name: formatExtraSourcesLabel(extra.sources - baseSources),
                    price: Number(extraPrice),
                    allowModify: true,
                    isExtraSources: true,
                })
            }
        }
        return { plan: offerName ?? "", details }
    })

    // ==================
    // ACTIONS SYNCHRONES
    // ==================

    /**
     * Initialise offres et modules et définit l'offre par défaut (la plus chère).
     */
    function setOffersAndModules(
        newOffers: OffersRecord,
        newModules: ModulesRecord
    ): void {
        offers.value = { ...newOffers }
        modules.value = { ...newModules }
        const keys = Object.keys(newOffers)
        if (keys.length && !offerSelected.value) {
            const sorted = [...keys].sort(
                (a, b) =>
                    (newOffers[b]?.price ?? 0) - (newOffers[a]?.price ?? 0)
            )
            setOfferSelected(sorted[0])
        }
    }

    /** Met à jour l'engagement. */
    function setCommitment(value: boolean): void {
        commitment.value = value
    }

    /**
     * Met à jour l'offre sélectionnée et retire les modules incompatibles.
     */
    function setOfferSelected(name: string): void {
        offerSelected.value = name
        const compatibleNames = getModulesFormatted.value.map((m) => m.name)
        moduleSelected.value = moduleSelected.value.filter((m) =>
            compatibleNames.includes(m)
        )
    }

    /**
     * Ajoute ou retire un module de la sélection.
     */
    function setModuleSelected(payload: {
        name: string
        selected: boolean
    }): void {
        if (payload.selected) {
            if (!moduleSelected.value.includes(payload.name)) {
                moduleSelected.value = [...moduleSelected.value, payload.name]
            }
        } else {
            moduleSelected.value = moduleSelected.value.filter(
                (m) => m !== payload.name
            )
        }
    }

    /** Plan de sources sélectionné (étape 2). */
    function setOfferSourceSelected(id: string): void {
        offerSourceSelected.value = id
    }

    /** Contexte pour le checkout (user, company, pipedrive). */
    function setPaymentContext(context: {
        user?: Record<string, unknown>
        company?: Record<string, unknown>
        pipedrive?: Record<string, unknown>
    }): void {
        paymentContext.value = { ...context }
    }

    /** Réinitialise les sélections (offre, modules, engagement, source). */
    function $resetSelection(): void {
        offerSelected.value = ""
        moduleSelected.value = []
        offerSourceSelected.value = ""
        commitment.value = true
    }

    /** Réinitialise tout le store. */
    function $reset(): void {
        clearLocalStorage()
        offers.value = {}
        modules.value = {}
        offerSelected.value = ""
        moduleSelected.value = []
        offerSourceSelected.value = ""
        commitment.value = true
        paymentContext.value = {}
        introFormUser.value = {
            firstname: "",
            lastname: "",
            username: "",
            phone: "",
            sector: null,
            agree: false,
        }
        introFormCompany.value = { name: "", type: "entreprise" }
        verifyUserResult.value = null
    }

    // ==================
    // ACTIONS ASYNCHRONES (useFetchWrapper)
    // ==================

    const {
        fetch: fetchStripeOffers,
        isFetching: isFetchingStripeOffers,
        hasError: hasErrorStripeOffers,
        error: errorStripeOffers,
    } = useFetchWrapper(async () => {
        if (!services?.$getStripeOffers) {
            throw new Error("Service $getStripeOffers non disponible")
        }
        const data = await services.$getStripeOffers()
        const rawOffers = data?.offers ?? {}
        const rawModules = data?.modules ?? {}
        if (Object.keys(rawOffers).length) {
            setOffersAndModules(rawOffers, rawModules)
        }
    })

    const {
        fetch: redirectToPayment,
        isFetching: isFetchingRedirectToPayment,
        hasError: hasErrorRedirectToPayment,
        error: errorRedirectToPayment,
    } = useFetchWrapper(async () => {
        if (!services?.$redirectToPayment) {
            throw new Error("Service $redirectToPayment non disponible")
        }
        const planId =
            getCheckoutPlanId.value || getSourcesPlans.value[0]?.id || ""
        const params = {
            planId,
            modulesId: getModuleIds.value.map((id) => ({ id })),
            user: paymentContext.value.user,
            company: paymentContext.value.company,
            pipedrive: paymentContext.value.pipedrive,
        }
        const url = await services.$redirectToPayment(params)
        if (url) {
            window.location.href = url
        }
    })

    /**
     * Vérifie si un utilisateur existe déjà avec cet email (étape intro).
     * 404 / not_found → verifyUserResult = false. 200 → true. Autre erreur → hasError pour le composant.
     */
    const {
        fetch: fetchVerifyUserAccountForPayment,
        isFetching: isFetchingVerifyUserAccountForPayment,
        hasError: hasErrorVerifyUserAccountForPayment,
        error: errorVerifyUserAccountForPayment,
    } = useFetchWrapper(async (kwargs?: { username?: string }) => {
        if (!services?.$verifyUserAccountForPayment) {
            throw new Error(
                "Service $verifyUserAccountForPayment non disponible"
            )
        }
        const username =
            kwargs?.username ?? introFormUser.value.username?.trim()
        if (!username) {
            throw new Error("Username requis pour la vérification")
        }
        try {
            const exists = await services.$verifyUserAccountForPayment({
                username,
            })
            verifyUserResult.value = exists
        } catch (err: unknown) {
            const status = (err as { status?: number })?.status
            const code = (err as { code?: string })?.code
            if (status === 404 || code === "not_found") {
                verifyUserResult.value = false
                return
            }
            throw err
        }
    })

    /**
     * Met à jour le deal Pipedrive avec l'offre sélectionnée (ex. EA, EM).
     * Appelé quand l'utilisateur quitte l'étape « choix d'offre » pour synchroniser le produit du deal.
     * No-op si pas de contactId (pas de contact Pipedrive créé à l'intro).
     */
    const {
        fetch: updatePipedriveDealForOffer,
        isFetching: isFetchingUpdatePipedriveDealForOffer,
        hasError: hasErrorUpdatePipedriveDealForOffer,
        error: errorUpdatePipedriveDealForOffer,
    } = useFetchWrapper(async () => {
        const pipedrive = paymentContext.value?.pipedrive as
            | { contactId?: number; dealId?: number; organisationId?: number }
            | undefined
        const contactId = pipedrive?.contactId
        if (contactId == null) {
            return
        }
        if (!services?.$updateContact) {
            throw new Error("Service $updateContact non disponible")
        }
        const user = introFormUser.value
        const company = introFormCompany.value
        const phone =
            ((user.phone ?? "").replace(/\D/g, "").trim() || user.phone) ?? ""
        await services.$updateContact({
            id: contactId,
            dealId: pipedrive?.dealId,
            firstname: user.firstname ?? "",
            lastname: user.lastname ?? "",
            username: user.username ?? "",
            phone,
            activitySector: user.sector,
            product: offerSelected.value || null,
            organization: company.name ?? "",
        })
    })

    function clearVerifyUserResult(): void {
        verifyUserResult.value = null
    }

    // ==================
    // PERSISTANCE LOCALSTORAGE
    // ==================

    /**
     * Sauvegarde l'état du formulaire (étape + données utilisateur) en localStorage.
     * À appeler depuis le composant à chaque changement d'étape (sauf Success/Failure).
     */
    function saveToLocalStorage(step: string): void {
        if (typeof window === "undefined") return
        const pc = paymentContext.value
        const pipedrive = pc?.pipedrive as
            | { contactId?: number; organisationId?: number; dealId?: number }
            | undefined
        const paymentContextToPersist: SubscriptionFormPersisted["paymentContext"] =
            {
                user: pc?.user ? { ...pc.user } : undefined,
                company: pc?.company ? { ...pc.company } : undefined,
                pipedrive:
                    pipedrive != null
                        ? {
                              contactId: pipedrive.contactId,
                              organisationId: pipedrive.organisationId,
                              dealId: pipedrive.dealId,
                          }
                        : undefined,
            }
        const persisted: SubscriptionFormPersisted = {
            step: step as SubscriptionFormPersistedStep,
            introFormUser: { ...introFormUser.value },
            introFormCompany: { ...introFormCompany.value },
            offerSelected: offerSelected.value,
            moduleSelected: [...moduleSelected.value],
            commitment: commitment.value,
            offerSourceSelected: offerSourceSelected.value,
            paymentContext: paymentContextToPersist,
            verifyUserResult: verifyUserResult.value,
        }
        try {
            window.localStorage.setItem(
                SUBSCRIPTION_FORM_STORAGE_KEY,
                JSON.stringify(persisted)
            )
        } catch {
            // localStorage indisponible (quota, mode privé, etc.)
        }
    }

    /**
     * Charge le brouillon depuis localStorage, réassigne les refs du store, et retourne les données (dont step).
     * Ne modifie pas offers/modules (rechargés via API). Retourne null si pas de brouillon ou données invalides.
     */
    function loadFromLocalStorage(): SubscriptionFormPersisted | null {
        if (typeof window === "undefined") return null
        try {
            const raw = window.localStorage.getItem(
                SUBSCRIPTION_FORM_STORAGE_KEY
            )
            if (!raw) return null
            const data = JSON.parse(raw) as unknown
            if (
                !data ||
                typeof data !== "object" ||
                !("step" in data) ||
                typeof (data as { step: string }).step !== "string"
            ) {
                return null
            }
            const step = (data as { step: string }).step
            const validSteps: SubscriptionFormPersistedStep[] = [
                "FormPaymentIntro",
                "FormPaymentOffer",
                "FormPaymentSources",
                "FormPaymentRecap",
            ]
            if (!validSteps.includes(step as SubscriptionFormPersistedStep)) {
                return null
            }
            const d = data as Record<string, unknown>
            if (d.introFormUser && typeof d.introFormUser === "object") {
                introFormUser.value = {
                    firstname: "",
                    lastname: "",
                    username: "",
                    phone: "",
                    sector: null,
                    agree: false,
                    ...(d.introFormUser as Record<string, unknown>),
                } as FormPaymentUserForm
            }
            if (d.introFormCompany && typeof d.introFormCompany === "object") {
                introFormCompany.value = {
                    name: "",
                    type: "entreprise",
                    ...(d.introFormCompany as Record<string, unknown>),
                } as FormPaymentCompanyForm
            }
            if (typeof d.offerSelected === "string") {
                offerSelected.value = d.offerSelected
            }
            if (Array.isArray(d.moduleSelected)) {
                moduleSelected.value = d.moduleSelected.filter(
                    (m): m is string => typeof m === "string"
                )
            }
            if (typeof d.commitment === "boolean") {
                commitment.value = d.commitment
            }
            if (typeof d.offerSourceSelected === "string") {
                offerSourceSelected.value = d.offerSourceSelected
            }
            if (d.paymentContext && typeof d.paymentContext === "object") {
                const pc = d.paymentContext as Record<string, unknown>
                const savedPipedrive = pc.pipedrive as
                    | {
                          contactId?: number
                          organisationId?: number
                          dealId?: number
                      }
                    | undefined
                paymentContext.value = {
                    user:
                        pc.user && typeof pc.user === "object"
                            ? { ...(pc.user as Record<string, unknown>) }
                            : undefined,
                    company:
                        pc.company && typeof pc.company === "object"
                            ? { ...(pc.company as Record<string, unknown>) }
                            : undefined,
                    pipedrive:
                        savedPipedrive != null
                            ? {
                                  contactId:
                                      typeof savedPipedrive.contactId ===
                                      "number"
                                          ? savedPipedrive.contactId
                                          : typeof savedPipedrive.contactId ===
                                              "string"
                                            ? Number(savedPipedrive.contactId)
                                            : undefined,
                                  organisationId:
                                      typeof savedPipedrive.organisationId ===
                                      "number"
                                          ? savedPipedrive.organisationId
                                          : typeof savedPipedrive.organisationId ===
                                              "string"
                                            ? Number(
                                                  savedPipedrive.organisationId
                                              )
                                            : undefined,
                                  dealId:
                                      typeof savedPipedrive.dealId === "number"
                                          ? savedPipedrive.dealId
                                          : typeof savedPipedrive.dealId ===
                                              "string"
                                            ? Number(savedPipedrive.dealId)
                                            : undefined,
                              }
                            : undefined,
                } as SubscriptionFormPersisted["paymentContext"]
            }
            if (
                d.verifyUserResult === null ||
                d.verifyUserResult === true ||
                d.verifyUserResult === false
            ) {
                verifyUserResult.value = d.verifyUserResult
            }
            return {
                step: step as SubscriptionFormPersistedStep,
                introFormUser: introFormUser.value,
                introFormCompany: introFormCompany.value,
                offerSelected: offerSelected.value,
                moduleSelected: [...moduleSelected.value],
                commitment: commitment.value,
                offerSourceSelected: offerSourceSelected.value,
                paymentContext: { ...paymentContext.value },
                verifyUserResult: verifyUserResult.value,
            }
        } catch {
            return null
        }
    }

    /**
     * Supprime le brouillon du formulaire du localStorage.
     */
    function clearLocalStorage(): void {
        if (typeof window === "undefined") return
        try {
            window.localStorage.removeItem(SUBSCRIPTION_FORM_STORAGE_KEY)
        } catch {
            // localStorage indisponible
        }
    }

    // ==================
    // EXPORTS
    // ==================

    return {
        // state
        offers,
        modules,
        offerSelected,
        moduleSelected,
        commitment,
        offerSourceSelected,
        paymentContext,
        introFormUser,
        introFormCompany,
        verifyUserResult,
        // getters
        getOffersFormatted,
        getModulesFormatted,
        getMaxDiscountPercent,
        getSourcesPlans,
        getCheckoutPlanId,
        getModuleIds,
        getSummary,
        // actions synchrones
        setOffersAndModules,
        setCommitment,
        setOfferSelected,
        setModuleSelected,
        setOfferSourceSelected,
        setPaymentContext,
        clearVerifyUserResult,
        $resetSelection,
        $reset,
        saveToLocalStorage,
        loadFromLocalStorage,
        clearLocalStorage,
        // actions asynchrones
        fetchStripeOffers,
        redirectToPayment,
        fetchVerifyUserAccountForPayment,
        updatePipedriveDealForOffer,
        // états de chargement / erreur (pour les composants)
        isFetchingStripeOffers,
        hasErrorStripeOffers,
        errorStripeOffers,
        isFetchingRedirectToPayment,
        hasErrorRedirectToPayment,
        errorRedirectToPayment,
        isFetchingVerifyUserAccountForPayment,
        hasErrorVerifyUserAccountForPayment,
        errorVerifyUserAccountForPayment,
        isFetchingUpdatePipedriveDealForOffer,
        hasErrorUpdatePipedriveDealForOffer,
        errorUpdatePipedriveDealForOffer,
    }
})
